import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useDialog } from "@/context/DialogContext";
import { Plus, Kanban, ArrowUpRight, DotsThree, Trash } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const STATUS_LABEL = {
  active: "Active",
  on_hold: "On Hold",
  completed: "Completed",
};

export default function Projects() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { confirm } = useDialog();
  const canCreate = user.role === "admin" || user.role === "manager" || user.role === "hr";
  const canDelete = can("delete_projects");
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", status: "active", members: [], end_date: "" });
  const [error, setError] = useState("");

  const load = async () => {
    const r = await api.get("/projects");
    setProjects(r.data);
    if (canCreate) {
      try {
        const u = await api.get("/users");
        setUsers(u.data);
      } catch {}
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/projects", { ...form, end_date: form.end_date || null });
      setShowCreate(false);
      setForm({ name: "", description: "", status: "active", members: [], end_date: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteProject = async (projectId, projectName) => {
    const confirmed = await confirm({
      title: "Delete Project",
      description: `Delete project "${projectName}"? This will also delete all tasks and files in this project. This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "destructive"
    });
    
    if (!confirmed) return;
    
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success("Project deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete project");
    }
  };

  const toggleMember = (id) => {
    setForm(f => ({
      ...f,
      members: f.members.includes(id) ? f.members.filter(m => m !== id) : [...f.members, id]
    }));
  };

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Delivery / Projects</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Projects</h1>
          <p className="text-sm text-muted-foreground mt-2">Track scope, teams and end-to-end delivery.</p>
        </div>
        {canCreate && (
          <button className="btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)} data-testid="create-project-button">
            <Plus size={16} weight="bold" /> New Project
          </button>
        )}
      </div>

      <div className="p-8 lg:p-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-border" data-testid="projects-grid">
          {projects.map((p) => (
            <div key={p.id} className="grid-cell p-6 group hover:bg-muted transition-colors relative" data-testid={`project-item-${p.id}`}>
              <Link to={`/projects/${p.id}`} className="absolute inset-0" aria-label={`Go to ${p.name}`} />
              <div className="flex items-start justify-between mb-4">
                <Kanban size={22} weight="bold" />
                <div className="flex items-center gap-2 relative z-10">
                  {canDelete && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button 
                          className="p-1 hover:bg-accent rounded transition-colors"
                          onClick={(e) => e.preventDefault()}
                        >
                          <DotsThree size={20} weight="bold" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          className="text-red-600 cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            deleteProject(p.id, p.name);
                          }}
                        >
                          <Trash size={16} weight="bold" className="mr-2" />
                          Delete Project
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <ArrowUpRight size={18} weight="bold" className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
              <div className="eyebrow mb-1">{STATUS_LABEL[p.status]}</div>
              <div className="font-display text-xl font-bold leading-tight">{p.name}</div>
              <div className="text-xs text-muted-foreground line-clamp-2 mt-2 min-h-[2rem]">{p.description || "No description"}</div>
              <div className="mt-6">
                <div className="flex items-center justify-between text-xs font-mono-mini mb-1">
                  <span>{p.progress}%</span>
                  <span>{p.done_count}/{p.task_count} tasks</span>
                </div>
                <div className="w-full h-1 bg-secondary">
                  <div className="h-1 bg-[#002FA7]" style={{ width: `${p.progress}%` }} />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="font-mono-mini text-muted-foreground">{p.members?.length || 0} members</span>
                {p.end_date && <span className="font-mono-mini text-muted-foreground">Due {p.end_date}</span>}
              </div>
            </div>
          ))}
          {projects.length === 0 && (
            <div className="col-span-full p-12 text-center text-sm text-muted-foreground">
              No projects yet.
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-border w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-project-dialog">
            <div className="eyebrow mb-2">Create</div>
            <h2 className="font-display text-2xl font-bold mb-6">New project</h2>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="eyebrow block mb-2">Name</label>
                <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="project-name-input" />
              </div>
              <div>
                <label className="eyebrow block mb-2">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="project-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">Status</label>
                  <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="project-status-select">
                    <option value="active">Active</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-2">End date</label>
                  <input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="project-end-date-input" />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-2">Members</label>
                <div className="border border-border max-h-40 overflow-y-auto" data-testid="project-members-list">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-2 border-b border-border hover:bg-muted cursor-pointer text-sm">
                      <input type="checkbox" checked={form.members.includes(u.id)} onChange={() => toggleMember(u.id)} data-testid={`member-checkbox-${u.id}`} />
                      <span>{u.name}</span>
                      <span className="text-muted-foreground text-xs ml-auto">{u.role}</span>
                    </label>
                  ))}
                </div>
              </div>
              {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
                <button type="submit" className="flex-1 btn-primary" data-testid="submit-project">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
